/**
 * «Главная» (client-cabinet-shell). Клиенту — сводка-дашборд (client-home-dashboard): алерт
 * «Требует внимания», счётчики-светофор обучения, блоки «Ближайшие сроки», «Мои заказы» и
 * «Документы». Данные — из тех же эндпоинтов, что и разделы «Обучение» (журнал сроков),
 * «Мои заказы» и «Документы»; owner-scoping — на бэкенде. У компании сводка включает и сроки
 * спецоценки (home-sout-deadlines) — из данных раздела «Спецоценка»: строка в алерте, вторая
 * строка счётчиков (рабочие места) и список под обучением в «Ближайших сроках». Физлицу
 * спецоценка недоступна и не запрашивается. Блоки грузятся независимо: сбой
 * одного запроса показывает ошибку в своём блоке и не закрывает остальные.
 * Команде — стартовая страница с переходами в «Очередь заказов» и «Клиенты».
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { AlertTriangle, CalendarClock, ClipboardList, FileText, Users } from 'lucide-react'
import {
  documentsApi,
  ordersApi,
  soutApi,
  trainingApi,
  type DocumentDTO,
  type JournalResponse,
  type ListResponse,
  type OrderCard,
  type SoutDashboard,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import {
  attentionText,
  deadlineDateLabel,
  fileTypeLabel,
  formatHomeDate,
  HOME_DOCUMENTS_LIMIT,
  HOME_ORDERS_LIMIT,
  soutAttentionText,
  urgentDeadlines,
  urgentSoutDeadlines,
  type UrgentDeadline,
  type UrgentSoutDeadline,
} from '@/lib/home'
import { clientBadge, clientOrderTarget, compositionSummary, serviceLabel } from '@/lib/orders'
import { SOUT_COUNTER_LABELS } from '@/lib/sout'
import { useTrainingOrderStart } from '@/training/training-order-start'
import { backState } from '@/lib/use-back-target'
import { useAuth } from '@/app/auth-context'
import { isTeamRole } from '@/app/nav'
import { useSeesAllClients } from '@/team/roles'
import { AppLink } from '@/app/navigation'
import { GreetingHeading } from '@/design-system/components/layout/greeting-heading'
import { Card } from '@/design-system/components/layout/card'
import { Grid } from '@/design-system/components/layout/grid'
import { ListItem } from '@/design-system/components/data/list-item'
import { StatGrid } from '@/design-system/components/data/stat-grid'
import { StatCard } from '@/design-system/components/data/stat-card'
import { Alert } from '@/design-system/components/feedback/alert'
import { DocIcon } from '@/design-system/components/feedback/doc-icon'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import { Badge } from '@/design-system/components/primitives/badge'
import { Button } from '@/design-system/components/primitives/button'
import { StatusDot } from '@/design-system/components/primitives/status-dot'

/**
 * Название строки списка переносится по словам, а не усекается: в двух узких колонках (чуть шире
 * контрольной точки) усечённое ФИО сжималось до одной буквы. `ListItem` усекает название сам
 * (`truncate`), поэтому перенос включается на вложенном элементе.
 */
const ROW_NAME_CLASS = 'whitespace-normal break-words'

/**
 * Ссылка-название строки: цвет основного текста (акцент — по наведению). Кольцо фокуса —
 * внутреннее: контейнер названия обрезает содержимое, внешнее кольцо обрезалось бы вместе с ним.
 */
const ROW_LINK_CLASS = `${ROW_NAME_CLASS} text-txt hover:text-accent focus-visible:ring-inset focus-visible:ring-offset-0`

/** Состояние независимой загрузки блока: пока нет ни данных, ни ошибки — блок грузится. */
interface Loaded<T> {
  data: T | null
  error: string | null
}

/**
 * Одноразовая загрузка данных блока при открытии страницы. `load` обязан быть стабильной ссылкой
 * (функция уровня модуля) — иначе запрос повторялся бы на каждый рендер.
 */
function useLoaded<T>(load: (signal: AbortSignal) => Promise<T>, fallback: string): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ data: null, error: null })
  useEffect(() => {
    const controller = new AbortController()
    load(controller.signal)
      .then((data) => {
        if (!controller.signal.aborted) setState({ data, error: null })
      })
      .catch((err) => {
        if (!controller.signal.aborted)
          setState({ data: null, error: getErrorMessage(err, fallback) })
      })
    return () => controller.abort()
  }, [load, fallback])
  return state
}

const loadJournal = (signal: AbortSignal): Promise<JournalResponse> => trainingApi.journal(signal)
/**
 * Сроки спецоценки — только компании: физлицу раздел «Спецоценка» недоступен, запрос не
 * отправляется (загрузчик сразу отдаёт `null`).
 */
const loadSout = (signal: AbortSignal): Promise<SoutDashboard | null> => soutApi.dashboard(signal)
const loadNoSout = (): Promise<SoutDashboard | null> => Promise.resolve(null)
const loadOrders = (signal: AbortSignal): Promise<ListResponse<OrderCard>> =>
  ordersApi.list({ limit: HOME_ORDERS_LIMIT, offset: 0 }, signal)
const loadDocuments = (signal: AbortSignal): Promise<ListResponse<DocumentDTO>> =>
  documentsApi.list({ limit: HOME_DOCUMENTS_LIMIT, offset: 0 }, signal)

/** Тело блока-карточки: ошибка / загрузка / пустое состояние / содержимое. */
function BlockBody<T>({
  state,
  isEmpty,
  empty,
  children,
}: {
  state: Loaded<T>
  isEmpty: (data: T) => boolean
  empty: ReactNode
  children: (data: T) => ReactNode
}) {
  if (state.error) return <Alert variant="danger">{state.error}</Alert>
  if (!state.data) return <p className="py-10 text-center text-txt-secondary">Загрузка…</p>
  if (isEmpty(state.data)) return <>{empty}</>
  return <>{children(state.data)}</>
}

/** Ссылка «в раздел» под списком блока. */
function BlockLink({ to, children }: { to: string; children: ReactNode }) {
  return (
    <div className="mt-3">
      <AppLink to={to}>{children} →</AppLink>
    </div>
  )
}

function DeadlineRow({
  deadline,
  staffLink,
}: {
  deadline: UrgentDeadline
  /** Карточка сотрудника есть только у компании — физлицу ФИО показывается текстом. */
  staffLink: boolean
}) {
  const location = useLocation()
  const expired = deadline.status === 'expired'
  const date = deadlineDateLabel(deadline)
  return (
    <ListItem
      leading={
        <StatusDot
          variant={expired ? 'red' : 'amber'}
          label={expired ? 'Просрочено' : 'Истекает скоро'}
          aria-hidden
        />
      }
      name={
        staffLink ? (
          <AppLink
            to={`/staff/${deadline.personId}`}
            state={backState(location)}
            className={ROW_LINK_CLASS}
          >
            {deadline.fullName}
          </AppLink>
        ) : (
          <span className={ROW_NAME_CLASS}>{deadline.fullName}</span>
        )
      }
      // Метка статуса — в строке подписи. Кнопки заказа в строке нет (ТЗ п.2.1): обучение
      // заказывают общей кнопкой блока «Ближайшие сроки».
      meta={
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge status={expired ? 'danger' : 'warning'}>{deadline.label}</Badge>
          <span>
            {[deadline.directionName, deadline.programName, date].filter(Boolean).join(' · ')}
          </span>
        </span>
      }
    />
  )
}

function SoutDeadlineRow({
  deadline,
  ordering,
  onOrder,
}: {
  deadline: UrgentSoutDeadline
  ordering: boolean
  onOrder: () => void
}) {
  const location = useLocation()
  const expired = deadline.status === 'expired'
  const date = deadlineDateLabel(deadline)
  return (
    <ListItem
      leading={
        <StatusDot
          variant={expired ? 'red' : 'amber'}
          label={expired ? 'Просрочено' : 'Истекает скоро'}
          aria-hidden
        />
      }
      name={
        <AppLink
          to={`/workplaces/${deadline.workplaceId}`}
          state={backState(location)}
          className={ROW_LINK_CLASS}
        >
          {deadline.position}
        </AppLink>
      }
      meta={
        <span className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          <Badge status={expired ? 'danger' : 'warning'}>{deadline.label}</Badge>
          <span>{[deadline.departmentName, date].filter(Boolean).join(' · ')}</span>
        </span>
      }
      action={
        <Button
          size="sm"
          variant="secondary"
          disabled={ordering}
          aria-label={`Заказать спецоценку: ${deadline.position}`}
          onClick={onOrder}
        >
          Заказать
        </Button>
      }
    />
  )
}

/** Подзаголовок части блока: «Ближайшие сроки» и счётчики у компании делятся на обучение и спецоценку. */
function SectionCaption({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <h3 className={`mb-2 text-13 font-medium text-txt-secondary ${className ?? ''}`}>{children}</h3>
  )
}

function OrderRowItem({ order }: { order: OrderCard }) {
  const badge = clientBadge(order.dataStatus)
  const summary =
    order.itemCount > 0 ? compositionSummary(order.service, order.itemCount) : 'ещё не заполнено'
  return (
    <ListItem
      name={
        <AppLink to={clientOrderTarget(order)} className={ROW_LINK_CLASS}>
          {serviceLabel(order.service)} · {summary}
        </AppLink>
      }
      meta={`Заказ № ${order.number} · от ${formatHomeDate(order.createdAt)}`}
      action={<Badge status={badge.role}>{badge.label}</Badge>}
    />
  )
}

function DocumentRow({ doc }: { doc: DocumentDTO }) {
  return (
    <ListItem
      leading={<DocIcon />}
      // Длинное название — до двух строк, целиком — во всплывающей подсказке.
      name={
        <span title={doc.title} className={`${ROW_NAME_CLASS} line-clamp-2`}>
          {doc.title}
        </span>
      }
      meta={`${fileTypeLabel(doc.title) ?? doc.typeName} · ${formatHomeDate(doc.date)}`}
      action={
        <Button asChild size="sm" variant="secondary">
          <a href={doc.downloadUrl} download aria-label={`Скачать: ${doc.title}`}>
            Скачать
          </a>
        </Button>
      }
    />
  )
}

function ClientHome({ individual }: { individual: boolean }) {
  const navigate = useNavigate()
  const journal = useLoaded(loadJournal, 'Не удалось загрузить сроки обучения')
  const sout = useLoaded(individual ? loadNoSout : loadSout, 'Не удалось загрузить сроки спецоценки')
  const orders = useLoaded(loadOrders, 'Не удалось загрузить заказы')
  const documents = useLoaded(loadDocuments, 'Не удалось загрузить документы')
  const [ordering, setOrdering] = useState(false)
  const orderingRef = useRef(false)
  const [orderError, setOrderError] = useState<string | null>(null)

  const counters = journal.data?.counters
  const attention = journal.data
    ? attentionText({
        overdue: journal.data.counters.overdue,
        soon: journal.data.counters.soon,
        threshold: journal.data.threshold,
        individual,
      })
    : null
  // Ни у кого нет ни одного действующего/истекающего/просроченного обучения (сотрудников нет либо
  // все «Нет данных») — сроков ещё не из чего считать; иначе пустой список значит «всё в порядке».
  const noTrainingData = !counters || counters.overdue + counters.soon + counters.ok === 0
  const deadlines = useMemo(
    () => (journal.data ? urgentDeadlines(journal.data.rows) : []),
    [journal.data],
  )

  const soutCounters = sout.data?.counters
  const soutAttention = sout.data
    ? soutAttentionText({
        overdue: sout.data.counters.overdue,
        soon: sout.data.counters.soon,
        threshold: sout.data.threshold,
      })
    : null
  const soutDeadlines = useMemo(
    () => (sout.data ? urgentSoutDeadlines(sout.data.rows) : []),
    [sout.data],
  )
  // Красный алерт — есть просрочка хоть по обучению, хоть по спецоценке.
  const anyOverdue = (counters?.overdue ?? 0) > 0 || (soutCounters?.overdue ?? 0) > 0

  // «Заказать спецоценку» — как в разделе «Спецоценка»: создаётся черновик, клиент попадает в его
  // заполнение. Кнопок на странице несколько, а `disabled` применяется только со следующим
  // рендером — повторный щелчок до него отсекает ref, иначе двойной щелчок создал бы два пустых
  // черновика.
  async function createSoutOrder() {
    if (orderingRef.current) return
    orderingRef.current = true
    setOrderError(null)
    setOrdering(true)
    try {
      const order = await ordersApi.create({ service: 'sout' })
      navigate(`/orders/${order.id}/sout`)
    } catch (err) {
      setOrderError(getErrorMessage(err, 'Не удалось создать заказ СОУТ'))
      orderingRef.current = false
      setOrdering(false)
    }
  }
  // Заказ обучения — с вопросом «Заполнить по сотрудникам с истекающим и просроченным
  // обучением?» (training-order-entry-points; ТЗ п.2.2).
  const trainingStart = useTrainingOrderStart(individual)
  const busy = ordering || trainingStart.starting

  return (
    <>
      {orderError || trainingStart.error ? (
        <Alert variant="danger" className="mb-4">
          {orderError ?? trainingStart.error}
        </Alert>
      ) : null}
      {trainingStart.dialog}

      {/* Красный — есть просрочка (обучение или спецоценка); только истекающее — спокойный
          информационный. Обучение и спецоценка — отдельными строками: единицы разные
          (сотрудники и рабочие места). Кнопка есть только у того, что требует внимания. */}
      {attention || soutAttention ? (
        <Alert
          variant={anyOverdue ? 'danger' : 'info'}
          className="mb-4 flex-wrap items-center"
          title="Требует внимания"
          icon={<AlertTriangle className="size-5 stroke-current" aria-hidden />}
          actions={
            // Две кнопки в ряд на узком экране сжимали бы текст алерта до столбика — там они
            // встают друг под другом.
            <div className="xl:flex-row flex flex-col gap-2">
              {attention ? (
                <Button onClick={() => trainingStart.start()} disabled={busy}>
                  Заказать обучение
                </Button>
              ) : null}
              {soutAttention ? (
                <Button
                  variant={attention ? 'secondary' : 'primary'}
                  onClick={() => void createSoutOrder()}
                  disabled={busy}
                >
                  Заказать спецоценку
                </Button>
              ) : null}
            </div>
          }
        >
          {attention ? <p>{attention}</p> : null}
          {soutAttention ? <p>{soutAttention}</p> : null}
        </Alert>
      ) : null}

      {/* Пока журнал не загружен (или не загрузился) — прочерки, а не нули: ноль читался бы
          как «просроченных нет». */}
      {individual ? null : <SectionCaption>Обучение — сотрудники</SectionCaption>}
      <StatGrid className="mb-4" role="group" aria-label="Сроки обучения">
        <StatCard
          label="Просрочено"
          value={counters?.overdue ?? '—'}
          status="danger"
          noData={!counters}
        />
        <StatCard
          label="Истекает скоро"
          value={counters?.soon ?? '—'}
          status="warning"
          noData={!counters}
        />
        <StatCard
          label="Всё в порядке"
          value={counters?.ok ?? '—'}
          status="success"
          noData={!counters}
        />
        <StatCard label="Нет данных" value={counters?.noData ?? '—'} variant="gray" noData />
      </StatGrid>

      {/* Спецоценка — те же числа, что в разделе «Спецоценка»; считаются рабочие места. Строка
          есть у компании всегда (до загрузки — прочерки), чтобы страница не прыгала. */}
      {individual ? null : (
        <>
          <SectionCaption>Спецоценка — рабочие места</SectionCaption>
          <StatGrid className="mb-4" role="group" aria-label="Сроки спецоценки">
            <StatCard
              label={SOUT_COUNTER_LABELS.overdue}
              value={soutCounters?.overdue ?? '—'}
              status="danger"
              noData={!soutCounters}
            />
            <StatCard
              label={SOUT_COUNTER_LABELS.soon}
              value={soutCounters?.soon ?? '—'}
              status="warning"
              noData={!soutCounters}
            />
            <StatCard
              label={SOUT_COUNTER_LABELS.valid}
              value={soutCounters?.valid ?? '—'}
              status="success"
              noData={!soutCounters}
            />
            <StatCard
              label={SOUT_COUNTER_LABELS.none}
              value={soutCounters?.none ?? '—'}
              variant="gray"
              noData
            />
          </StatGrid>
        </>
      )}

      <Grid cols={2} className="items-start">
        {/* Заказ обучения — общей кнопкой блока, а не в строке каждого сотрудника (ТЗ п.2.1):
            заявка всё равно оформляется целиком. */}
        <Card
          header={
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span>Ближайшие сроки</span>
              {deadlines.length > 0 ? (
                <Button size="sm" variant="secondary" onClick={() => trainingStart.start()} disabled={busy}>
                  Заказать обучение
                </Button>
              ) : null}
            </div>
          }
        >
          {individual ? null : <SectionCaption>Обучение</SectionCaption>}
          <BlockBody
            state={journal}
            isEmpty={() => deadlines.length === 0}
            empty={
              noTrainingData ? (
                <EmptyState
                  icon={CalendarClock}
                  title="Пока нет данных по срокам"
                  description={
                    individual
                      ? 'Сроки появятся после обучения'
                      : 'Сроки появятся после обучения сотрудников'
                  }
                  className="py-10"
                />
              ) : (
                <EmptyState
                  icon={CalendarClock}
                  title="Ближайших сроков нет"
                  description="Просроченного и истекающего обучения нет"
                  className="py-10"
                />
              )
            }
          >
            {() =>
              deadlines.map((d) => (
                <DeadlineRow key={d.key} deadline={d} staffLink={!individual} />
              ))
            }
          </BlockBody>
          <BlockLink to="/training">
            {individual ? 'Все сроки обучения' : 'Все сотрудники и сроки'}
          </BlockLink>

          {/* Спецоценка — отдельным списком под обучением, а не вперемешку: срочная спецоценка
              не вытесняет из блока сроки обучения, и наоборот. */}
          {individual ? null : (
            <div className="mt-5 border-t border-line pt-4">
              <SectionCaption>Спецоценка</SectionCaption>
              <BlockBody
                state={sout}
                isEmpty={() => soutDeadlines.length === 0}
                empty={
                  <p className="py-2 text-txt-secondary">
                    Просроченной и истекающей спецоценки нет
                  </p>
                }
              >
                {() =>
                  soutDeadlines.map((d) => (
                    <SoutDeadlineRow
                      key={d.key}
                      deadline={d}
                      ordering={busy}
                      onOrder={() => void createSoutOrder()}
                    />
                  ))
                }
              </BlockBody>
              <BlockLink to="/sout">Все рабочие места и сроки</BlockLink>
            </div>
          )}
        </Card>

        <div className="flex flex-col gap-4">
          <Card header="Мои заказы">
            <BlockBody
              state={orders}
              isEmpty={(data) => data.items.length === 0}
              empty={
                <EmptyState
                  icon={ClipboardList}
                  title="Заказов пока нет"
                  description="Здесь появятся ваши заказы"
                  className="py-10"
                />
              }
            >
              {(data) => data.items.map((o) => <OrderRowItem key={o.id} order={o} />)}
            </BlockBody>
            <BlockLink to="/orders">Все заказы</BlockLink>
          </Card>

          <Card header="Документы">
            <BlockBody
              state={documents}
              isEmpty={(data) => data.items.length === 0}
              empty={
                <EmptyState
                  icon={FileText}
                  title="Документов пока нет"
                  description="Готовые файлы заказов и документы обучения появятся здесь"
                  className="py-10"
                />
              }
            >
              {(data) => data.items.map((d) => <DocumentRow key={d.id} doc={d} />)}
            </BlockBody>
            <BlockLink to="/documents">Все документы</BlockLink>
          </Card>
        </div>
      </Grid>
    </>
  )
}

/**
 * «Главная» команды — стартовая страница с переходами в рабочие разделы. Сводки по заказам здесь
 * нет: прежняя заглушка всегда писала «Очередь пуста», даже когда заказы в очереди были.
 */
function TeamHome() {
  const seesAll = useSeesAllClients()
  return (
    <Grid cols={2}>
      <Card header="Очередь заказов">
        <EmptyState
          icon={ClipboardList}
          title="Заказы клиентов"
          description={
            seesAll
              ? 'Заявки на проверке и заказы в работе — по всем клиентам'
              : 'Заказы, где вы ответственный, и заказы ваших клиентов'
          }
          action={<AppLink to="/team/orders">Открыть очередь заказов →</AppLink>}
          className="py-10"
        />
      </Card>
      <Card header="Клиенты">
        <EmptyState
          icon={Users}
          title="Карточки клиентов"
          description="Реквизиты, структура, сотрудники и экология каждого клиента"
          action={<AppLink to="/team/clients">Открыть список клиентов →</AppLink>}
          className="py-10"
        />
      </Card>
    </Grid>
  )
}

export function HomePage() {
  const { context } = useAuth()
  if (!context) return null
  const team = isTeamRole(context.user.role)
  const individual = context.user.role === 'individual'

  return (
    <div>
      {/* Без имени (home-greeting-without-name): у компании в fullName — название организации. */}
      <GreetingHeading>Здравствуйте!</GreetingHeading>
      <p className="mb-5 mt-1 text-txt-secondary">
        {team
          ? 'Заказы и клиенты Центрики'
          : individual
            ? 'Вот что происходит с вашим обучением и заказами'
            : 'Вот что происходит с обучением и заказами вашей организации'}
      </p>
      {team ? <TeamHome /> : <ClientHome individual={individual} />}
    </div>
  )
}
